<script>
import { mapGetters } from 'vuex'
import { apolloClient } from '../../graphql/apollo'

let deferredPrompt

export default {
  name: 'TheNavbar',
  props: {
    items: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      isOpen: false,
      installPwaButtonVisible: true
    }
  },
  computed: {
    ...mapGetters(['isUserLoggedIn', 'isAdmin'])
  },
  methods: {
    async logout() {
      await this.$store.dispatch('logout')
      apolloClient.clearStore().then(async () => {
        await apolloClient.resetStore()
      })
      this.$router.replace({ name: 'LoginView' })
    },
    installPwa() {
      this.installPwaButtonVisible = false
      deferredPrompt.prompt()
      deferredPrompt.userChoice.then(choiceResult => {
        if (choiceResult.outcome === 'accepted') {
          console.log('User accepted the A2HS prompt')
        } else {
          console.log('User dismissed the A2HS prompt')
        }
        deferredPrompt = null
      })
    }
  },
  mounted() {
    window.addEventListener('beforeinstallprompt', e => {
      e.preventDefault()
      deferredPrompt = e
      this.installPwaButtonVisible = true
    })
  },
  watch: {
    $route(to, from) {
      this.isOpen = false
    }
  }
}
</script>

<template>
  <nav
    class="navbar is-shadowless is-fixed-top"
    role="navigation"
    aria-label="main navigation"
  >
    <div class="container">
      <div v-if="isUserLoggedIn" class="navbar-brand">
        <a class="navbar-item" href="/">
          <img class="logo is-hidden-print" src="@/assets/img/logo.png" />
          <img class="logo is-shown-print" src="@/assets/img/logo-wide.png" />
        </a>
        <div class="navbar-item">
          <button
            v-if="installPwaButtonVisible"
            class="button is-small is-rounded is-secondary is-hidden-tablet is-hidden-standalone"
            @click="installPwa"
          >
            Install app
          </button>
        </div>
        <a
          role="button"
          class="navbar-burger button is-white has-text-secondary is-pulled-right is-hidden-print"
          aria-label="menu"
          aria-expanded="false"
          data-target="navbarBasicExample"
          @click="isOpen = !isOpen"
        >
          <i class="fas fa-bars fa-lg" />
        </a>
      </div>

      <div
        v-else
        class="navbar-brand"
        style="flex-grow: 1;justify-content: center;"
      >
        <a class="navbar-item" href="https://www.plas.me">
          <img class="logo" src="@/assets/img/logo-wide.png" />
        </a>
      </div>

      <div
        v-if="isUserLoggedIn"
        class="navbar-menu is-shadowless"
        :class="{ 'is-active': isOpen }"
      >
        <div class="navbar-end">
          <div v-if="isAdmin" class="navbar-item">
            <router-link
              :to="{ name: 'BiomarkerListAdminView' }"
              :class="{ 'is-fullwidth': isOpen }"
            >
              <i class="fas fa-tools" />
              Admin
            </router-link>
          </div>
          <div v-for="item in items" :key="item.id" class="navbar-item">
            <router-link
              :class="{
                'is-active is-danger': $route.path === item.path,
                'is-fullwidth': isOpen
              }"
              tag="a"
              :to="item.path"
            >
              <i :class="`fas fa-${item.icon}`" />
              {{ item.name }}
            </router-link>
          </div>
          <div class="navbar-item has-dropdown is-hoverable">
            <a
              :class="{ 'is-fullwidth': isOpen }"
              class="navbar-link is-arrowless is-hidden-mobile"
            >
              <i class="fas fa-user-circle" />
            </a>
            <div ref="accountDropdown" class="navbar-dropdown is-boxed">
              <router-link
                class="navbar-item"
                :class="{ 'is-fullwidth': isOpen }"
                to="/settings"
                >Settings</router-link
              >
              <router-link
                class="navbar-item"
                :class="{ 'is-fullwidth': isOpen }"
                to="/billing"
                >Billing</router-link
              >
              <router-link
                class="navbar-item"
                :class="{ 'is-fullwidth': isOpen }"
                to="/security"
                >Password</router-link
              >
              <hr class="navbar-divider" />
              <a
                class="navbar-item"
                :class="{ 'is-fullwidth': isOpen }"
                @click="logout"
                >Log out</a
              >
            </div>
          </div>
          <div class="navbar-item">
            <router-link
              :to="{ name: 'AddLabReportView' }"
              tag="button"
              class="button is-rounded is-secondary"
              >Add lab report</router-link
            >
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<style lang="sass" scoped>
.navbar
  padding: 0 1.5rem
  border-bottom: 1.5px solid #eee
  .logo
    max-height: 32px
  .navbar-burger
    height: auto
    width: auto
    margin: 10px 10px 10px auto
  .navbar-menu
    overflow-x: visible
    overflow-y: visible
.navbar-item a
  font-weight: 400
  font-size: 1.2em
  color: #0d6f7a
  .svg-inline--fa
    margin: 0 5px
</style>
